import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, MessageCircle, Facebook, Instagram, Youtube } from 'lucide-react';

export const Footer = () => {
  const { t } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="bg-[#2C2C2C] text-[#FAF9F7] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand Column */}
          <div>
            <img 
              src="https://static.wixstatic.com/media/d66aed_4807542ab6c84dbbaec1a45677872d75~mv2.png/v1/fill/w_120,h_120,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/POMTT%2520LOGO%2520-PNG%2520white_edited.png"
              alt="POMTrips Logo"
              className="h-20 w-20 object-contain mb-4"
            />
            <p className="text-[#C4B5A0] font-serif text-xl mb-2">{t('footerTagline')}</p>
            <p className="text-[#8B7D6B] text-sm mb-4">
              POMTrips – Peace of Mind Trips and Travels
            </p>
            
            {/* Social Media Links */}
            <div className="flex space-x-4 mt-6">
              <a
                href="https://www.facebook.com/pomtripstravels/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#8B7D6B]/20 hover:bg-[#1877F2] p-2 rounded-full transition-all duration-300 group"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 text-[#C4B5A0] group-hover:text-white transition-colors" />
              </a>
              <a
                href="https://www.instagram.com/pomtripstravels?r=nametag"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#8B7D6B]/20 hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] p-2 rounded-full transition-all duration-300 group"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 text-[#C4B5A0] group-hover:text-white transition-colors" />
              </a>
              <a
                href="https://youtube.com/channel/UCJbibrKgQsWLGrHGXV9R3_g"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#8B7D6B]/20 hover:bg-[#FF0000] p-2 rounded-full transition-all duration-300 group"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5 text-[#C4B5A0] group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="font-medium text-lg mb-4 text-[#C4B5A0]">{t('footerQuickLinks')}</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('about')} className="text-[#8B7D6B] hover:text-[#C4B5A0] transition-colors">
                  {t('about')}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('experiences')} className="text-[#8B7D6B] hover:text-[#C4B5A0] transition-colors">
                  {t('experiences')}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('destinations')} className="text-[#8B7D6B] hover:text-[#C4B5A0] transition-colors">
                  {t('destinations')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-medium text-lg mb-4 text-[#C4B5A0]">{t('footerContact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-[#C4B5A0] mt-1 flex-shrink-0" />
                <span className="text-[#8B7D6B] text-sm">{t('footerAddress')}</span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-[#C4B5A0] flex-shrink-0" />
                <a href="tel:+917827884419" className="text-[#8B7D6B] hover:text-[#C4B5A0] transition-colors">
                  +91-782-788-4419
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-[#C4B5A0] flex-shrink-0" />
                <a href="mailto:contact@pomtrips.com" className="text-[#8B7D6B] hover:text-[#C4B5A0] transition-colors">
                  contact@pomtrips.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[#8B7D6B]/30">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-[#8B7D6B] text-sm">{t('footerCopyright')}</p>
            
            {/* Social Media Links - Bottom */}
            <div className="flex items-center space-x-4">
              <span className="text-[#8B7D6B] text-sm">Follow us:</span>
              <a
                href="https://www.facebook.com/pomtripstravels/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#8B7D6B] hover:text-[#1877F2] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/pomtripstravels?r=nametag"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#8B7D6B] hover:text-[#E4405F] transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://youtube.com/channel/UCJbibrKgQsWLGrHGXV9R3_g"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#8B7D6B] hover:text-[#FF0000] transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/917827884419"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-[#25D366] hover:bg-[#20BA5A] text-white p-4 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 z-50 group"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="h-6 w-6" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-[#2C2C2C] text-white px-3 py-2 rounded-md text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          {t('chatWithUs')}
        </span>
      </a>
    </footer>
  );
};